import { Byte32Array, CoordinateSystem } from '@awayjs/core';
import { ContextGLBlendFactor } from '../base/ContextGLBlendFactor';
import { ContextGLDrawMode } from '../base/ContextGLDrawMode';
import { ContextGLClearMask } from '../base/ContextGLClearMask';
import { ContextGLCompareMode } from '../base/ContextGLCompareMode';
import { ContextGLMipFilter } from '../base/ContextGLMipFilter';
import { ContextGLStencilAction } from '../base/ContextGLStencilAction';
import { ContextGLTextureFilter } from '../base/ContextGLTextureFilter';
import { ContextGLTriangleFace } from '../base/ContextGLTriangleFace';
import { ContextGLWrapMode } from '../base/ContextGLWrapMode';
import { OpCodes } from './OpCodes';
import { CubeTextureGLES } from './CubeTextureGLES';
import { IndexBufferGLES } from './IndexBufferGLES';
import { ProgramGLES } from './ProgramGLES';
import { TextureGLES } from './TextureGLES';
import { VertexBufferGLES } from './VertexBufferGLES';
import { GLESConnector } from './GLESConnector';
var ContextGLES = /** @class */ (function () {
    function ContextGLES(canvas) {
        this._blendFactorDictionary = new Object();
        this._drawModeDictionary = new Object();
        this._compareModeDictionary = new Object();
        this.stencilTriangleFace = new Object();
        this._stencilActionDictionary = new Object();
        this._textureIndexDictionary = new Array(8);
        this._textureTypeDictionary = new Object();
        this._wrapDictionary = new Object();
        this._filterDictionary = new Object();
        this._mipmapFilterDictionary = new Object();
        this._vertexBufferPropertiesDictionary = [];
        this.hasVao = false;
        this._samplerStates = new Array(8);
        this._stencilReferenceValue = 0;
        this._stencilReadMask = 0xff;
        this._separateStencil = false;
        this._textureCnt = 0;
        this._programCnt = 0;
        this._cubeTextureCnt = 0;
        this._indexBufferCnt = 0;
        this._vertexBufferCnt = 0;
        this._container = canvas;
        this._cmdBytes = new Byte32Array();
        this._createBytes = new Byte32Array();
        ContextGLES._soundBytes = new Byte32Array();
        ContextGLES._soundBytes.writeUnsignedInt(100); //id for sending sound data
        this._cmdBytes.writeUnsignedInt(0); //id for sending cmd data
        this._createBytes.writeUnsignedInt(1); //id for sending create data
        this._blendFactorDictionary[ContextGLBlendFactor.ONE] = 0;
        this._blendFactorDictionary[ContextGLBlendFactor.DESTINATION_ALPHA] = 1;
        this._blendFactorDictionary[ContextGLBlendFactor.DESTINATION_COLOR] = 2;
        this._blendFactorDictionary[ContextGLBlendFactor.ONE_MINUS_DESTINATION_ALPHA] = 3;
        this._blendFactorDictionary[ContextGLBlendFactor.ONE_MINUS_DESTINATION_COLOR] = 4;
        this._blendFactorDictionary[ContextGLBlendFactor.ONE_MINUS_SOURCE_ALPHA] = 5;
        this._blendFactorDictionary[ContextGLBlendFactor.ONE_MINUS_SOURCE_COLOR] = 6;
        this._blendFactorDictionary[ContextGLBlendFactor.SOURCE_ALPHA] = 7;
        this._blendFactorDictionary[ContextGLBlendFactor.SOURCE_COLOR] = 8;
        this._blendFactorDictionary[ContextGLBlendFactor.ZERO] = 9;
        this._drawModeDictionary[ContextGLDrawMode.LINES] = 0;
        this._drawModeDictionary[ContextGLDrawMode.TRIANGLES] = 1;
        this._compareModeDictionary[ContextGLCompareMode.ALWAYS] = 0;
        this._compareModeDictionary[ContextGLCompareMode.EQUAL] = 1;
        this._compareModeDictionary[ContextGLCompareMode.GREATER] = 2;
        this._compareModeDictionary[ContextGLCompareMode.GREATER_EQUAL] = 3;
        this._compareModeDictionary[ContextGLCompareMode.LESS] = 4;
        this._compareModeDictionary[ContextGLCompareMode.LESS_EQUAL] = 5;
        this._compareModeDictionary[ContextGLCompareMode.NEVER] = 6;
        this._compareModeDictionary[ContextGLCompareMode.NOT_EQUAL] = 7;
        this.stencilTriangleFace[ContextGLTriangleFace.BACK] = 2;
        this.stencilTriangleFace[ContextGLTriangleFace.FRONT] = 1;
        this.stencilTriangleFace[ContextGLTriangleFace.FRONT_AND_BACK] = 0;
        //
        this._stencilActionDictionary[ContextGLStencilAction.DECREMENT_SATURATE] = 0;
        this._stencilActionDictionary[ContextGLStencilAction.DECREMENT_WRAP] = 1;
        this._stencilActionDictionary[ContextGLStencilAction.INCREMENT_SATURATE] = 2;
        this._stencilActionDictionary[ContextGLStencilAction.INCREMENT_WRAP] = 3;
        this._stencilActionDictionary[ContextGLStencilAction.INVERT] = 4;
        this._stencilActionDictionary[ContextGLStencilAction.KEEP] = 5;
        this._stencilActionDictionary[ContextGLStencilAction.SET] = 6;
        this._stencilActionDictionary[ContextGLStencilAction.ZERO] = 7;
        this._textureTypeDictionary['texture2d'] = 0;
        this._textureTypeDictionary['textureCube'] = 1;
        this._wrapDictionary[ContextGLWrapMode.REPEAT] = 0;
        this._wrapDictionary[ContextGLWrapMode.CLAMP] = 1;
        this._filterDictionary[ContextGLTextureFilter.LINEAR] = 0;
        this._filterDictionary[ContextGLTextureFilter.NEAREST] = 1;
        this._mipmapFilterDictionary[ContextGLTextureFilter.LINEAR] = new Object();
        this._mipmapFilterDictionary[ContextGLTextureFilter.LINEAR][ContextGLMipFilter.MIPNEAREST] = 2;
        this._mipmapFilterDictionary[ContextGLTextureFilter.LINEAR][ContextGLMipFilter.MIPLINEAR] = 3;
        this._mipmapFilterDictionary[ContextGLTextureFilter.LINEAR][ContextGLMipFilter.MIPNONE] = 0;
        this._mipmapFilterDictionary[ContextGLTextureFilter.NEAREST] = new Object();
        this._mipmapFilterDictionary[ContextGLTextureFilter.NEAREST][ContextGLMipFilter.MIPNEAREST] = 4;
        this._mipmapFilterDictionary[ContextGLTextureFilter.NEAREST][ContextGLMipFilter.MIPLINEAR] = 5;
        this._mipmapFilterDictionary[ContextGLTextureFilter.NEAREST][ContextGLMipFilter.MIPNONE] = 1;
    }
    ContextGLES.prototype.createVao = function () {
        return null;
    };
    // soundcommands are excuted on @away/player/AS2SoundAdapter, and stored here as binary data
    // when context is executed, they get send to java along woth the oother data
    ContextGLES.startSound = function (name, id, volume, loop) {
        ContextGLES._soundBytes.writeInt(OpCodes.startSound | ((loop ? 1 : 0) << 8));
        ContextGLES._soundBytes.writeFloat(volume);
        ContextGLES._soundBytes.writeInt(id);
        ContextGLES._soundBytes.writeUTFBytes(name);
    };
    ContextGLES.stopSound = function (id) {
        ContextGLES._soundBytes.writeInt(OpCodes.stopSound);
        ContextGLES._soundBytes.writeInt(id);
    };
    ContextGLES.updateSound = function (id, volume, loop) {
        ContextGLES._soundBytes.writeInt(OpCodes.updateSound | ((loop ? 1 : 0) << 8));
        ContextGLES._soundBytes.writeFloat(volume);
        ContextGLES._soundBytes.writeInt(id);
    };
    Object.defineProperty(ContextGLES.prototype, "container", {
        get: function () {
            return this._container;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ContextGLES.prototype, "standardDerivatives", {
        get: function () {
            return this._standardDerivatives;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ContextGLES.prototype, "glVersion", {
        get: function () {
            return 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ContextGLES.prototype, "pixelRatio", {
        get: function () {
            return 1;
        },
        enumerable: true,
        configurable: true
    });
    ContextGLES.prototype.enableStencil = function () {
        this._cmdBytes.writeInt(OpCodes.enableStencil);
    };
    ContextGLES.prototype.disableStencil = function () {
        this._cmdBytes.writeInt(OpCodes.disableStencil);
    };
    ContextGLES.prototype.gl = function () {
        return this._gl;
    };
    ContextGLES.prototype.clear = function (red, green, blue, alpha, depth, stencil, mask) {
        if (red === void 0) { red = 0; }
        if (green === void 0) { green = 0; }
        if (blue === void 0) { blue = 0; }
        if (alpha === void 0) { alpha = 1; }
        if (depth === void 0) { depth = 1; }
        if (stencil === void 0) { stencil = 0; }
        if (mask === void 0) { mask = ContextGLClearMask.ALL; }
        this._cmdBytes.writeInt(OpCodes.clear);
        this._cmdBytes.writeFloat(red);
        this._cmdBytes.writeFloat(green);
        this._cmdBytes.writeFloat(blue);
        this._cmdBytes.writeFloat(alpha);
        this._cmdBytes.writeInt(depth);
        this._cmdBytes.writeInt(stencil);
        this._cmdBytes.writeInt(mask);
    };
    ContextGLES.prototype.configureBackBuffer = function (width, height, antiAlias, enableDepthAndStencil) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        this._width = width;
        this._height = height;
        this._cmdBytes.writeInt(OpCodes.configureBackBuffer | (enableDepthAndStencil ? 1 : 0) << 8);
        this._cmdBytes.writeFloat(width);
        this._cmdBytes.writeFloat(height);
    };
    ContextGLES.prototype.createCubeTexture = function (size, format, optimizeForRenderToTexture, streamingLevels) {
        if (streamingLevels === void 0) { streamingLevels = 0; }
        // todo: cubetextures not finished / tested for opengl yet
        return new CubeTextureGLES(this, this._gl, size, this._cubeTextureCnt++);
    };
    ContextGLES.prototype.createIndexBuffer = function (numIndices) {
        // todo: indexBuffer not finished / tested for opengl yet
        return new IndexBufferGLES(this, this._gl, numIndices, this._indexBufferCnt++);
    };
    ContextGLES.prototype.createProgram = function () {
        return new ProgramGLES(this, this._gl, this._programCnt++);
    };
    ContextGLES.prototype.createTexture = function (width, height, format, optimizeForRenderToTexture, streamingLevels) {
        if (streamingLevels === void 0) { streamingLevels = 0; }
        //TODO streaming
        return new TextureGLES(this, this._gl, width, height, this._textureCnt++);
    };
    ContextGLES.prototype.createVertexBuffer = function (numVertices, dataPerVertex) {
        return new VertexBufferGLES(this, this._gl, numVertices, dataPerVertex, this._vertexBufferCnt++);
    };
    ContextGLES.prototype.dispose = function () {
        this._cmdBytes.writeInt(OpCodes.disposeContext);
        this.execute();
    };
    ContextGLES.prototype.drawToBitmapImage2D = function (destination) {
        //todo (not needed for icycle (?) )
        // var pixels:Uint8ClampedArray = new Uint8ClampedArray(destination.width*destination.height*4);
        //
        // this._gl.readPixels(0, 0, destination.width, destination.height, this._gl.RGBA, this._gl.UNSIGNED_BYTE, pixels);
        //
        // destination.setPixels(new Rectangle(0, 0, destination.width, destination.height), pixels);
    };
    ContextGLES.prototype.drawIndices = function (mode, indexBuffer, firstIndex, numIndices) {
        if (firstIndex === void 0) { firstIndex = 0; }
        if (numIndices === void 0) { numIndices = -1; }
        //todo (not needed for icycle)
        //GLESConnector.gles.drawIndices(mode, indexBuffer.id, firstIndex, numIndices);
        // if (!this._drawing)
        // 	throw "Need to clear before drawing if the buffer has not been cleared since the last present() call.";
        //
        //
        // this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, indexBuffer.glBuffer);
        // this._gl.drawElements(this._drawModeDictionary[mode], (numIndices == -1)? indexBuffer.numIndices : numIndices, this._gl.UNSIGNED_SHORT, firstIndex*2);
    };
    ContextGLES.prototype.drawVertices = function (mode, firstVertex, numVertices) {
        if (firstVertex === void 0) { firstVertex = 0; }
        if (numVertices === void 0) { numVertices = -1; }
        // if (!this._drawing)
        // 	throw "Need to clear before drawing if the buffer has not been cleared since the last present() call.";
        this._cmdBytes.writeInt(OpCodes.drawVertices | (this._drawModeDictionary[mode] << 8));
        this._cmdBytes.writeInt(firstVertex);
        this._cmdBytes.writeInt(numVertices);
    };
    ContextGLES.prototype.present = function () {
        this.execute();
    };
    ContextGLES.prototype.setBlendFactors = function (sourceFactor, destinationFactor) {
        this._blendEnabled = true;
        this._blendSourceFactor = this._blendFactorDictionary[sourceFactor];
        this._blendDestinationFactor = this._blendFactorDictionary[destinationFactor];
        this.updateBlendStatus();
    };
    ContextGLES.prototype.setColorMask = function (red, green, blue, alpha) {
        this._cmdBytes.writeInt(OpCodes.setColorMask);
        this._cmdBytes.writeInt((red ? 1 : 0) | (green ? 1 : 0) << 8 | (blue ? 1 : 0) << 16 | (alpha ? 1 : 0) << 24);
    };
    ContextGLES.prototype.setCulling = function (triangleFaceToCull, coordinateSystem) {
        if (coordinateSystem === void 0) { coordinateSystem = CoordinateSystem.LEFT_HANDED; }
        if (triangleFaceToCull == ContextGLTriangleFace.NONE) {
            //this.addStream(String.fromCharCode(OpCodes.disableCulling) + "#END");
            this._cmdBytes.writeInt(OpCodes.disableCulling);
            return;
        }
        var faceCulling = this.translateTriangleFace(triangleFaceToCull, coordinateSystem);
        this._cmdBytes.writeInt(OpCodes.setCulling | faceCulling << 8);
    };
    ContextGLES.prototype.setDepthTest = function (depthMask, passCompareMode) {
        this._cmdBytes.writeInt(OpCodes.setDepthTest | (depthMask ? 1 : 0) << 8 | this._compareModeDictionary[passCompareMode] << 16);
    };
    ContextGLES.prototype.setStencilActionsMasks = function (compareMode, referenceValue, writeMask, actionOnBothPass, actionOnDepthFail, actionOnDepthPassStencilFail, coordinateSystem) {
        if (actionOnBothPass === void 0) { actionOnBothPass = 'keep'; }
        if (actionOnDepthFail === void 0) { actionOnDepthFail = 'keep'; }
        if (actionOnDepthPassStencilFail === void 0) { actionOnDepthPassStencilFail = 'keep'; }
        if (coordinateSystem === void 0) { coordinateSystem = 'leftHanded'; }
        var compareModeGL = this._compareModeDictionary[compareMode];
        var fail = this._stencilActionDictionary[actionOnDepthPassStencilFail];
        var zFail = this._stencilActionDictionary[actionOnDepthFail];
        var pass = this._stencilActionDictionary[actionOnBothPass];
        this._cmdBytes.writeInt(OpCodes.setStencilActionsMasks | (compareModeGL << 8));
        this._cmdBytes.writeInt(referenceValue);
        this._cmdBytes.writeInt(writeMask | fail << 8 | zFail << 16 | pass << 24);
    };
    ContextGLES.prototype.setStencilActions = function (triangleFace, compareMode, actionOnBothPass, actionOnDepthFail, actionOnDepthPassStencilFail, coordinateSystem) {
        if (triangleFace === void 0) { triangleFace = ContextGLTriangleFace.FRONT_AND_BACK; }
        if (compareMode === void 0) { compareMode = ContextGLCompareMode.ALWAYS; }
        if (actionOnBothPass === void 0) { actionOnBothPass = ContextGLStencilAction.KEEP; }
        if (actionOnDepthFail === void 0) { actionOnDepthFail = ContextGLStencilAction.KEEP; }
        if (actionOnDepthPassStencilFail === void 0) { actionOnDepthPassStencilFail = ContextGLStencilAction.KEEP; }
        if (coordinateSystem === void 0) { coordinateSystem = CoordinateSystem.LEFT_HANDED; }
        var compareModeGL = this._compareModeDictionary[compareMode];
        var triangleFaceGL = this.stencilTriangleFace[triangleFace];
        var fail = this._stencilActionDictionary[actionOnDepthPassStencilFail];
        var zFail = this._stencilActionDictionary[actionOnDepthFail];
        var pass = this._stencilActionDictionary[actionOnBothPass];
        this._cmdBytes.writeInt(OpCodes.setStencilActions | triangleFaceGL << 8);
        this._cmdBytes.writeInt(compareModeGL | fail << 8 | zFail << 16 | pass << 24);
    };
    ContextGLES.prototype.setStencilReferenceValue = function (referenceValue, readMask, writeMask) {
        if (readMask === void 0) { readMask = 0xFF; }
        if (writeMask === void 0) { writeMask = 0xFF; }
        this._cmdBytes.writeInt(OpCodes.setStencilReferenceValue);
        this._cmdBytes.writeInt(referenceValue);
        this._cmdBytes.writeInt(readMask);
        this._cmdBytes.writeInt(writeMask);
    };
    ContextGLES.prototype.setProgram = function (program) {
        this._cmdBytes.writeInt(OpCodes.setProgram);
        this._cmdBytes.writeInt(program.id);
    };
    ContextGLES.prototype.setProgramConstantsFromArray = function (programType, data) {
        this._cmdBytes.writeInt(OpCodes.setProgramConstant | programType << 8);
        this._cmdBytes.writeInt(data.length);
        this._cmdBytes.writeFloat32Array(data);
    };
    ContextGLES.prototype.setScissorRectangle = function (rectangle) {
        if (rectangle) {
            this._cmdBytes.writeInt(OpCodes.setScissorRect);
            this._cmdBytes.writeFloat(rectangle.x);
            this._cmdBytes.writeFloat(rectangle.y);
            this._cmdBytes.writeFloat(rectangle.width);
            this._cmdBytes.writeFloat(rectangle.height);
        }
        else {
            this._cmdBytes.writeInt(OpCodes.clearScissorRect);
        }
    };
    ContextGLES.prototype.setTextureAt = function (sampler, texture) {
        if (texture) {
            this._cmdBytes.writeInt(OpCodes.setTextureAt | sampler << 8);
            this._cmdBytes.writeInt(texture.id);
        }
        else {
            this._cmdBytes.writeInt(OpCodes.clearTextureAt | sampler << 8);
        }
    };
    ContextGLES.prototype.setSamplerStateAt = function (sampler, wrap, filter, mipfilter) {
        this._cmdBytes.writeInt(OpCodes.setSamplerStateAt | sampler << 8);
        this._cmdBytes.writeInt(this._wrapDictionary[wrap] | this._filterDictionary[filter] << 8 | this._mipmapFilterDictionary[filter][mipfilter] << 16);
    };
    ContextGLES.prototype.setVertexBufferAt = function (index, buffer, bufferOffset, format) {
        if (bufferOffset === void 0) { bufferOffset = 0; }
        if (format === void 0) { format = 4; }
        if (buffer) {
            this._cmdBytes.writeInt(OpCodes.setVertexBufferAt | index << 8 | format << 16 | buffer.dataPerVertex << 24);
            this._cmdBytes.writeInt(buffer.id);
            this._cmdBytes.writeInt(bufferOffset);
        }
        else {
            this._cmdBytes.writeInt(OpCodes.clearVertexBufferAt | index << 8);
        }
    };
    ContextGLES.prototype.setRenderToTexture = function (target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = false; }
        if (antiAlias === void 0) { antiAlias = 0; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        if (target === null || target === undefined) {
            this._cmdBytes.writeInt(OpCodes.clearRenderToTexture);
        }
        else {
            this._cmdBytes.writeInt(OpCodes.setRenderToTexture | (enableDepthAndStencil ? 1 : 0) << 8);
            this._cmdBytes.writeInt(target.id);
        }
        // var texture:TextureGLES = <TextureGLES> target;
        // var frameBuffer:WebGLFramebuffer = texture.frameBuffer;
        // this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, frameBuffer);
        //
        // if (enableDepthAndStencil) {
        // 	this._gl.enable(this._gl.STENCIL_TEST);
        // 	this._gl.enable(this._gl.DEPTH_TEST);
        // }
        //
        // this._gl.viewport(0, 0, texture.width, texture.height );
    };
    ContextGLES.prototype.setRenderToBackBuffer = function () {
        this._cmdBytes.writeInt(OpCodes.clearRenderToTexture);
        //todo
        // this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, null);
    };
    ContextGLES.prototype.copyToTexture = function (target, rect, destPoint) {
    };
    ContextGLES.prototype.updateBlendStatus = function () {
        if (this._blendEnabled) {
            this._cmdBytes.writeInt(OpCodes.setBlendFactors | this._blendSourceFactor << 8 | this._blendDestinationFactor << 16);
        }
        else {
            this._cmdBytes.writeUnsignedInt(OpCodes.disableBlending);
        }
    };
    ContextGLES.prototype.translateTriangleFace = function (triangleFace, coordinateSystem) {
        switch (triangleFace) {
            case ContextGLTriangleFace.BACK:
                return (coordinateSystem == CoordinateSystem.LEFT_HANDED) ? 2 : 1;
            case ContextGLTriangleFace.FRONT:
                return (coordinateSystem == CoordinateSystem.LEFT_HANDED) ? 1 : 2;
            case ContextGLTriangleFace.FRONT_AND_BACK:
                return 0;
            default:
                throw 'Unknown ContextGLTriangleFace type.'; // TODO error
        }
    };
    ContextGLES.prototype.execute = function () {
        if (this._createBytes.bytePosition > 4) {
            this._createBytes.bytePosition = 0;
            var localInt32View1 = new Int32Array(this._createBytes.byteLength / 4);
            this._createBytes.readInt32Array(localInt32View1);
            GLESConnector.gles.sendGLESCommands(localInt32View1.buffer);
            this._createBytes.byteLength = 0;
            this._createBytes.bytePosition = 0;
            this._createBytes.writeUnsignedInt(1); // make sure first int in bytearray is the id (1 for create bytes)
        }
        if (this._cmdBytes.bytePosition > 4) {
            this._cmdBytes.bytePosition = 0;
            var localInt32View = new Int32Array(this._cmdBytes.byteLength / 4);
            this._cmdBytes.readInt32Array(localInt32View);
            GLESConnector.gles.sendGLESCommands(localInt32View.buffer);
            this._cmdBytes.byteLength = 0;
            this._cmdBytes.bytePosition = 0;
            this._cmdBytes.writeUnsignedInt(0); // make sure first int in bytearray is the id (0 for cmd bytes)
        }
        if (ContextGLES._soundBytes.bytePosition > 4) {
            ContextGLES._soundBytes.bytePosition = 0;
            var localInt32View2 = new Int32Array(ContextGLES._soundBytes.byteLength / 4);
            ContextGLES._soundBytes.readInt32Array(localInt32View2);
            GLESConnector.gles.sendGLESCommands(localInt32View2.buffer);
            ContextGLES._soundBytes.byteLength = 0;
            ContextGLES._soundBytes.bytePosition = 0;
            ContextGLES._soundBytes.writeUnsignedInt(100); // make sure first int in bytearray is the id (100 for cmd soundbytes)
        }
    };
    ContextGLES.prototype.finish = function () { };
    ContextGLES._uniformLocationNameDictionary = ['fc', 'fs', 'vc'];
    ContextGLES.MAX_SAMPLERS = 8;
    ContextGLES.modulo = 0;
    return ContextGLES;
}());
export { ContextGLES };
var VertexBufferProperties = /** @class */ (function () {
    function VertexBufferProperties(size, type, normalized) {
        this.size = size;
        this.type = type;
        this.normalized = normalized;
    }
    return VertexBufferProperties;
}());
export { VertexBufferProperties };
